
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Clock, MapPin, CheckCircle, Play } from 'lucide-react';
import type { Visit } from '@/types';

interface VisitsListProps {
  visits: Visit[];
  onClockIn: (visitId: string) => void;
  locationLoading: boolean;
  hasLocation: boolean;
  hasActiveTimeRecord: boolean;
}

const VisitsList: React.FC<VisitsListProps> = ({ 
  visits, 
  onClockIn, 
  locationLoading, 
  hasLocation, 
  hasActiveTimeRecord 
}) => {
  const formatTime = (timeString: string) => {
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getVisitStatusColor = (status: string) => {
    switch (status) {
      case 'scheduled': return 'text-blue-600 bg-blue-100';
      case 'in_progress': return 'text-green-600 bg-green-100';
      case 'completed': return 'text-gray-600 bg-gray-100';
      case 'cancelled': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center">
          <Clock className="h-5 w-5 mr-2" />
          Today's Visits ({visits.length})
        </CardTitle>
      </CardHeader>
      <CardContent>
        {visits.length === 0 ? (
          <div className="text-center py-8">
            <Clock className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No visits scheduled for today</p>
          </div>
        ) : (
          <div className="space-y-4">
            {visits.map((visit) => (
              <div key={visit.id} className="border rounded-lg p-4 hover:bg-gray-50">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <div className="bg-blue-100 p-2 rounded-full">
                        <Clock className="h-4 w-4 text-blue-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold">
                          {visit.patient?.first_name} {visit.patient?.last_name}
                        </h4>
                        <p className="text-sm text-gray-500">
                          {formatTime(visit.scheduled_start_time)} - {formatTime(visit.scheduled_end_time)}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4 text-sm text-gray-600">
                        <span className={`px-2 py-1 rounded-full text-xs ${getVisitStatusColor(visit.status)}`}>
                          {visit.status.replace('_', ' ').toUpperCase()}
                        </span>
                        {visit.patient?.address && (
                          <div className="flex items-center">
                            <MapPin className="h-3 w-3 mr-1" />
                            <span className="truncate max-w-xs">{visit.patient.address}</span>
                          </div>
                        )}
                      </div>
                      
                      {visit.status === 'scheduled' && !hasActiveTimeRecord && (
                        <Button 
                          onClick={() => onClockIn(visit.id)}
                          disabled={locationLoading || !hasLocation}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <Play className="h-4 w-4 mr-2" />
                          {locationLoading ? 'Getting Location...' : 'Clock In'}
                        </Button>
                      )}
                      
                      {visit.status === 'completed' && (
                        <div className="flex items-center text-green-600">
                          <CheckCircle className="h-4 w-4 mr-1" />
                          <span className="text-sm">Completed</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default VisitsList;
